import { useEffect } from 'react';
import { ActiveProps } from './index';
import { useCompletedActiveSchema } from './use-completed-active-schema';

export type UseSilentAwakenProps = Omit<ActiveProps, 'ampExtra'> & { hasFulfilled?: boolean };
type Awaited<T extends Promise<unknown>> = T extends Promise<infer U> ? U : never;
export type RelatedApps = Awaited<ReturnType<NonNullable<typeof window.navigator.getInstalledRelatedApps>>> | undefined;

interface relatedAppData {
  id?: string;
  platform: string;
  version?: string;
  url?: string;
}

const DEFAULT_TIMEOUT = 10000;
const LEMON8_PKG_ID = 'com.bd.nproject';

const delay = (timeout: number) => new Promise<undefined>((resolve) => setTimeout(resolve, timeout));

const getIsClient = (): boolean => {
  return typeof window !== 'undefined';
};

const getRelatedApps = async (): Promise<RelatedApps> => {
  if (!getIsClient()) {
    return Promise.resolve(undefined);
  }

  if (window?.navigator?.getInstalledRelatedApps) {
    return Promise.race([window.navigator.getInstalledRelatedApps(), delay(DEFAULT_TIMEOUT)]);
  }
  return Promise.resolve(undefined);
};

const awakenAfterDelay = async (schema: string): Promise<void> => {
  await delay(DEFAULT_TIMEOUT);
  location.href = schema;
};

const awakenApp = async (schema: string): Promise<void> => {
  const relatedAppsInfo = await getRelatedApps();
  if (relatedAppsInfo && relatedAppsInfo.length > 0) {
    const lemon8AppInfo: relatedAppData = relatedAppsInfo.find((app) => app.id.includes(LEMON8_PKG_ID));
    if (lemon8AppInfo) {
      window.Tea('wap_with_app', {
        installed_app_name: lemon8AppInfo.id,
        installed_app_version: lemon8AppInfo.version,
      });
      await awakenAfterDelay(schema);
    }
  } else {
    await awakenAfterDelay(schema);
  }
};

export const useSilentAwaken = (props: UseSilentAwakenProps) => {
  const { schemaOption, hasFulfilled = false } = props;

  const pid = 'website_seo_silent_awaken';

  const schema = useCompletedActiveSchema(
    {
      ...schemaOption,
      pid,
    },
    {
      enterPosition: 'silent_awaken',
    },
  );

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const isAndroid = /android/i.test(ua);

    if (isAndroid) {
      // wait for the ending of stream rendering
      if (hasFulfilled) {
        awakenApp(schema).catch((err) => {
          console.error('awaken app error', err);
        });
      }
    }
  }, [hasFulfilled]);
};
